import { useEffect, useState } from 'react';
import { TrashIcon } from '@bizfirst/common-react';
import type { AgentDTO, AgentFormValues } from '../../types';
import { useAgentAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { Modal } from '../Shared/Modal';
import { ConfirmDialog } from '../Shared/ConfirmDialog';
import { LookupSelect } from '../Shared/LookupSelect';
import { InstructionsPanel } from './InstructionsPanel';
import { TemplatesPanel } from './TemplatesPanel';
import { RulesPanel } from './RulesPanel';
import { HooksPanel } from './HooksPanel';
import { CapabilitiesPanel } from './CapabilitiesPanel';
import { QuotasPanel } from './QuotasPanel';
import { PoliciesPanel } from './PoliciesPanel';
import { PaymentRulesPanel } from './PaymentRulesPanel';
import { DataSamplesPanel } from './DataSamplesPanel';
import { ScopesPanel } from './ScopesPanel';
import { VersionsPanel } from './VersionsPanel';
import { UnverifiedTabDot } from '../Shared/UnverifiedBanner';

export interface AgentEditorProps {
  /** Omit to create a new agent; pass an existing AgentID to edit. */
  agentId?: number;
  onClose: () => void;
  onSaved: (agent: AgentDTO) => void;
  /**
   * Narrow screens: the section tabs become a side panel opened from a hamburger button, instead of a
   * horizontal row that scrolls sideways. Defaults false, so the row is unchanged for other consumers.
   * The narrow-screen layout itself comes from the host stylesheet (see .aaa-editor-tabs-compact).
   */
  compactTabs?: boolean;
}

type CoreTab = 'basic' | 'content' | 'category' | 'llm' | 'advanced' | 'security';
type SubEntityTab = 'instructions' | 'templates' | 'rules' | 'hooks' | 'capabilities' | 'quotas' | 'policies' | 'paymentRules' | 'dataSamples' | 'scopes' | 'versions';
type Tab = CoreTab | SubEntityTab;

/** `tier` drives the "Unverified" tab-strip dot + in-panel banner (see UnverifiedBanner.tsx) —
 *  'confirmed' tabs (Instructions/Templates/Rules/Hooks) have real production seed data behind
 *  their corrected field/route shapes; 'unverified' tabs (Phase 3's remaining 7) were verified the
 *  same way against the live backend but have no confirmed real production data yet. */
const SUB_ENTITY_TABS: Array<{ id: SubEntityTab; label: string; tier: 'confirmed' | 'unverified' }> = [
  { id: 'instructions', label: 'Instructions', tier: 'confirmed' },
  { id: 'templates', label: 'Templates', tier: 'confirmed' },
  { id: 'rules', label: 'Rules', tier: 'confirmed' },
  { id: 'hooks', label: 'Hooks', tier: 'confirmed' },
  { id: 'capabilities', label: 'Capabilities', tier: 'unverified' },
  { id: 'quotas', label: 'Quotas', tier: 'unverified' },
  { id: 'policies', label: 'Policies', tier: 'unverified' },
  { id: 'paymentRules', label: 'Payment Rules', tier: 'unverified' },
  { id: 'dataSamples', label: 'Data Samples', tier: 'unverified' },
  { id: 'scopes', label: 'Scopes', tier: 'unverified' },
  { id: 'versions', label: 'Versions', tier: 'unverified' },
];

const CORE_TABS: Array<{ id: CoreTab; label: string }> = [
  { id: 'basic', label: 'Basic Info' },
  { id: 'content', label: 'Content' },
  { id: 'category', label: 'Category' },
  { id: 'llm', label: 'LLM Configuration' },
  { id: 'advanced', label: 'Advanced Settings' },
  { id: 'security', label: 'Security' },
];

const DEFAULT_FORM: AgentFormValues = {
  Name: '',
  Description: '',
  ShortDescription: '',
  LongDescription: '',
  IconUrl: '',
  ImageUrl: '',
  AgentTypeID: null,
  AgentCategoryID: null,
  AgentClassificationID: null,
  LlmProviderID: null,
  LlmModelID: null,
  LlmModelVersion: '',
  Temperature: null,
  MaxTokens: null,
  LlmMaxOutputTokens: null,
  LlmMaxRecursionDepth: null,
  LlmReasoningEffortLevel: '',
  LlmMaxMessageLimit: null,
  PerformanceOptimizationIntensityID: null,
  ResponseQualityIntensityID: null,
  CompactMessageCount: null,
  CompactTokenCount: null,
  McpEnabled: false,
  FunctionEnabled: false,
  KnowledgeEnabled: false,
  ConnectorID: null,
  PrimaryConnectorGroupID: null,
  ChannelGroupID: null,
  IsRouter: false,
  IsPlanner: false,
  IsTask: false,
  IsTaskUtility: false,
  IsMergeUtility: false,
  IsProcess: false,
  IsProcessElement: false,
  IsTemplate: false,
  MessageEncryptionEnforced: false,
  OutgoingMessageDecryptionEnforced: false,
  MessageSigningEnforced: false,
  OutgoingMessageSigningEnforced: false,
  IsPublic: false,
  IsAnonymous: false,
  IsGlobal: false,
  Disabled: false,
  Enabled: true,
  Installed: true,
};

/**
 * Full tabbed Agent editor — adapted from flow-studio-designer's AgentEditor.tsx (2,347 lines).
 * V1 scope decision (octopus-admin-spec Open Question 3): brings over the 6 core tabs
 * (Basic Info/Content/Category/LLM Configuration/Advanced Settings/Security) plus Instructions —
 * the one sub-entity tab confirmed to have real, fully-wired CRUD (including working delete) in
 * the source editor.
 *
 * Phase 2 restored 3 of the dropped ~9 sub-entity tabs — Templates, Rules, and Hooks — the 3
 * confirmed to have real production seed data, per corrected field/route shapes found by reading
 * the live BizFirstPayrollV3 backend directly (see TemplatesPanel.tsx/RulesPanel.tsx/
 * HooksPanel.tsx and their API clients' own header comments for the specifics, and
 * DevelopmentHistoryLog.md for the full audit). They're wired in here as standard, fully-trusted
 * tabs, same as Instructions — `tier: 'confirmed'` on SUB_ENTITY_TABS, no "Unverified" marker.
 *
 * Phase 3 (this pass) restores the remaining 7 — Capabilities, Quotas, Policies, Payment Rules,
 * Data Samples, Scopes, and Versions (the last read-only) — each carrying `tier: 'unverified'`
 * and a visible "Unverified" tab-strip dot + in-panel banner (UnverifiedBanner.tsx), since none of
 * the seven have confirmed real production data the way Phase 2's three do. Their field/route
 * shapes were nonetheless verified with the same rigor directly against the live backend, not
 * assumed from the task briefing or the design doc — see each panel's own header comment and
 * DevelopmentHistoryLog.md for the drift found. Blockchain tab (3 checkboxes:
 * EnableBlockchainLogging/EnableBlockchainControl/EnableIpfsKnowledgeBase) remains deferred as
 * explicitly low-priority per the original task's instruction to skip "obviously advanced/
 * rarely-used" tabs.
 *
 * Delete (top-level Agent) is new here — see AgentAdminApiClient.deleteById's comment.
 */
export function AgentEditor({ agentId, onClose, onSaved, compactTabs = false }: AgentEditorProps) {
  const api = useAgentAdminApiClient();
  const [navOpen, setNavOpen] = useState(false);
  const isEditing0 = !agentId;
  const [activeTab, setActiveTab] = useState<Tab>('basic');
  const [form, setForm] = useState<AgentFormValues>(DEFAULT_FORM);
  const [loading, setLoading] = useState(!!agentId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    setLoading(true);
    api.getById(agentId).then((result) => {
      if (cancelled) return;
      if (result.success && result.data) {
        setForm({ ...DEFAULT_FORM, ...result.data });
      } else {
        setError(result.error?.message ?? 'Failed to load agent.');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const set = <K extends keyof AgentFormValues>(key: K, value: AgentFormValues[K]) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!form.Name.trim()) {
      setError('Agent name is required.');
      setActiveTab('basic');
      return;
    }
    if (form.Temperature != null && (Number.isNaN(form.Temperature) || form.Temperature < 0 || form.Temperature > 2)) {
      setError('Temperature must be between 0 and 2.');
      setActiveTab('llm');
      return;
    }
    setSaving(true);
    setError(null);
    const result = agentId ? await api.update(agentId, form) : await api.create(form);
    setSaving(false);
    if (result.success && result.data) {
      onSaved(result.data);
    } else {
      setError(result.error?.message ?? 'Failed to save agent.');
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!agentId) return;
    setDeleting(true);
    const result = await api.deleteById(agentId);
    setDeleting(false);
    if (result.success) {
      onClose();
    } else {
      setError(result.error?.message ?? 'Failed to delete agent.');
      setConfirmDelete(false);
    }
  };

  const tabs: Array<{ id: Tab; label: string; tier?: 'confirmed' | 'unverified' }> = agentId ? [...CORE_TABS, ...SUB_ENTITY_TABS] : CORE_TABS;

  return (
    <Modal isOpen title={agentId ? `Edit Agent — ${form.Name || '…'}` : 'Create Agent'} onClose={onClose} width={860}>
      <div className="aaa-editor">
        {compactTabs && (
          <button type="button" className="aaa-editor-nav-toggle" aria-expanded={navOpen} onClick={() => setNavOpen((open) => !open)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <path d="M2 4h12M2 8h12M2 12h12" />
            </svg>
            <span className="aaa-editor-nav-current">{tabs.find((t) => t.id === activeTab)?.label}</span>
          </button>
        )}
        <nav className={`aaa-editor-tabs${compactTabs ? ' aaa-editor-tabs-compact' : ''}${navOpen ? ' is-open' : ''}`}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`aaa-tab${activeTab === tab.id ? ' active' : ''}`}
              onClick={() => {
                setActiveTab(tab.id);
                setNavOpen(false);
              }}
            >
              {tab.label}
              {tab.tier === 'unverified' && <UnverifiedTabDot />}
            </button>
          ))}
        </nav>
        {compactTabs && navOpen && <div className="aaa-editor-nav-backdrop" aria-hidden="true" onClick={() => setNavOpen(false)} />}

        <div className="aaa-editor-body">
          {loading && <div className="aaa-editor-loading">Loading…</div>}
          {!loading && error && <div className="aaa-editor-error">{error}</div>}

          {!loading && activeTab === 'basic' && (
            <div className="aaa-form-grid">
              <div className="aaa-form-group aaa-form-group-full">
                <label>Name *</label>
                <input type="text" maxLength={300} value={form.Name} onChange={(e) => set('Name', e.target.value)} />
              </div>
              <div className="aaa-form-group aaa-form-group-full">
                <label>Description</label>
                <textarea rows={3} maxLength={2000} value={form.Description ?? ''} onChange={(e) => set('Description', e.target.value)} />
              </div>
            </div>
          )}

          {!loading && activeTab === 'content' && (
            <div className="aaa-form-grid">
              <div className="aaa-form-group aaa-form-group-full">
                <label>Short Description</label>
                <input type="text" maxLength={500} placeholder="Brief summary (max 500 chars)" value={form.ShortDescription ?? ''} onChange={(e) => set('ShortDescription', e.target.value)} />
              </div>
              <div className="aaa-form-group aaa-form-group-full">
                <label>Long Description</label>
                <textarea rows={6} maxLength={4000} placeholder="Detailed description (max 4000 chars)" value={form.LongDescription ?? ''} onChange={(e) => set('LongDescription', e.target.value)} />
              </div>
              <div className="aaa-form-group">
                <label>Icon URL</label>
                <input type="text" maxLength={500} placeholder="URL to icon image (SVG or PNG)" value={form.IconUrl ?? ''} onChange={(e) => set('IconUrl', e.target.value)} />
              </div>
              <div className="aaa-form-group">
                <label>Image URL</label>
                <input type="text" maxLength={500} placeholder="URL to main image" value={form.ImageUrl ?? ''} onChange={(e) => set('ImageUrl', e.target.value)} />
              </div>
              {form.ImageUrl && (
                <div className="aaa-form-group-full">
                  <img src={form.ImageUrl} alt="" style={{ maxHeight: 120, borderRadius: 6 }} onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
                </div>
              )}
            </div>
          )}

          {!loading && activeTab === 'category' && (
            <div className="aaa-form-grid">
              <LookupSelect lookupKey="agentType" label="Agent Type" value={form.AgentTypeID} onChange={(v) => set('AgentTypeID', v)} />
              <LookupSelect lookupKey="agentCategory" label="Agent Category" value={form.AgentCategoryID} onChange={(v) => set('AgentCategoryID', v)} />
              <LookupSelect lookupKey="agentClassification" label="Agent Classification" value={form.AgentClassificationID} onChange={(v) => set('AgentClassificationID', v)} />

              <fieldset className="aaa-form-group-full aaa-fieldset">
                <legend>Features</legend>
                <div className="aaa-checkbox-grid">
                  <Checkbox label="MCP Enabled" checked={!!form.McpEnabled} onChange={(v) => set('McpEnabled', v)} />
                  <Checkbox label="Function Enabled" checked={!!form.FunctionEnabled} onChange={(v) => set('FunctionEnabled', v)} />
                  <Checkbox label="Knowledge Enabled" checked={!!form.KnowledgeEnabled} onChange={(v) => set('KnowledgeEnabled', v)} />
                </div>
              </fieldset>

              <fieldset className="aaa-form-group-full aaa-fieldset">
                <legend>Agent Roles</legend>
                <div className="aaa-checkbox-grid">
                  <Checkbox label="Router" checked={!!form.IsRouter} onChange={(v) => set('IsRouter', v)} />
                  <Checkbox label="Planner" checked={!!form.IsPlanner} onChange={(v) => set('IsPlanner', v)} />
                  <Checkbox label="Task" checked={!!form.IsTask} onChange={(v) => set('IsTask', v)} />
                  <Checkbox label="Task Utility" checked={!!form.IsTaskUtility} onChange={(v) => set('IsTaskUtility', v)} />
                  <Checkbox label="Merge Utility" checked={!!form.IsMergeUtility} onChange={(v) => set('IsMergeUtility', v)} />
                  <Checkbox label="Process" checked={!!form.IsProcess} onChange={(v) => set('IsProcess', v)} />
                  <Checkbox label="Process Element" checked={!!form.IsProcessElement} onChange={(v) => set('IsProcessElement', v)} />
                  <Checkbox label="Template" checked={!!form.IsTemplate} onChange={(v) => set('IsTemplate', v)} />
                </div>
              </fieldset>
            </div>
          )}

          {!loading && activeTab === 'llm' && (
            <div className="aaa-form-grid">
              <LookupSelect lookupKey="llmProvider" label="LLM Provider" value={form.LlmProviderID} onChange={(v) => set('LlmProviderID', v)} />
              <LookupSelect lookupKey="llmModel" label="LLM Model" value={form.LlmModelID} onChange={(v) => set('LlmModelID', v)} />
              <div className="aaa-form-group">
                <label>Model Version</label>
                <input type="text" maxLength={50} value={form.LlmModelVersion ?? ''} onChange={(e) => set('LlmModelVersion', e.target.value)} />
              </div>
              <div className="aaa-form-group">
                <label>Temperature</label>
                <input
                  type="number"
                  step={0.01}
                  min={0}
                  max={2}
                  value={form.Temperature ?? ''}
                  onChange={(e) => set('Temperature', e.target.value ? parseFloat(e.target.value) : null)}
                />
              </div>
              <NumberField label="Max Tokens" value={form.MaxTokens} onChange={(v) => set('MaxTokens', v)} />
              <NumberField label="Max Output Tokens" value={form.LlmMaxOutputTokens} onChange={(v) => set('LlmMaxOutputTokens', v)} />
              <NumberField label="Max Recursion Depth" value={form.LlmMaxRecursionDepth} onChange={(v) => set('LlmMaxRecursionDepth', v)} />
              <NumberField label="Max Message Limit" value={form.LlmMaxMessageLimit} onChange={(v) => set('LlmMaxMessageLimit', v)} />
              <div className="aaa-form-group">
                <label>Reasoning Effort Level</label>
                <input type="text" maxLength={50} value={form.LlmReasoningEffortLevel ?? ''} onChange={(e) => set('LlmReasoningEffortLevel', e.target.value)} />
              </div>
            </div>
          )}

          {!loading && activeTab === 'advanced' && (
            <div className="aaa-form-grid">
              <LookupSelect lookupKey="performanceIntensity" label="Performance Optimization Intensity" value={form.PerformanceOptimizationIntensityID} onChange={(v) => set('PerformanceOptimizationIntensityID', v)} />
              <LookupSelect lookupKey="qualityIntensity" label="Response Quality Intensity" value={form.ResponseQualityIntensityID} onChange={(v) => set('ResponseQualityIntensityID', v)} />
              <NumberField label="Compact Message Count" value={form.CompactMessageCount} onChange={(v) => set('CompactMessageCount', v)} />
              <NumberField label="Compact Token Count" value={form.CompactTokenCount} onChange={(v) => set('CompactTokenCount', v)} />
              <LookupSelect lookupKey="connector" label="Connector" value={form.ConnectorID} onChange={(v) => set('ConnectorID', v)} />
              <LookupSelect lookupKey="connectorGroup" label="Primary Connector Group" value={form.PrimaryConnectorGroupID} onChange={(v) => set('PrimaryConnectorGroupID', v)} />
              <LookupSelect lookupKey="channelGroup" label="Channel Group" value={form.ChannelGroupID} onChange={(v) => set('ChannelGroupID', v)} />
            </div>
          )}

          {!loading && activeTab === 'security' && (
            <div className="aaa-form-grid">
              <fieldset className="aaa-form-group-full aaa-fieldset">
                <legend>Message Security</legend>
                <div className="aaa-checkbox-grid">
                  <Checkbox label="Message Encryption Enforced" checked={!!form.MessageEncryptionEnforced} onChange={(v) => set('MessageEncryptionEnforced', v)} />
                  <Checkbox label="Outgoing Message Decryption Enforced" checked={!!form.OutgoingMessageDecryptionEnforced} onChange={(v) => set('OutgoingMessageDecryptionEnforced', v)} />
                  <Checkbox label="Message Signing Enforced" checked={!!form.MessageSigningEnforced} onChange={(v) => set('MessageSigningEnforced', v)} />
                  <Checkbox label="Outgoing Message Signing Enforced" checked={!!form.OutgoingMessageSigningEnforced} onChange={(v) => set('OutgoingMessageSigningEnforced', v)} />
                </div>
              </fieldset>
              <fieldset className="aaa-form-group-full aaa-fieldset">
                <legend>Status</legend>
                <div className="aaa-checkbox-grid">
                  <Checkbox label="Public" checked={!!form.IsPublic} onChange={(v) => set('IsPublic', v)} />
                  <Checkbox label="Anonymous" checked={!!form.IsAnonymous} onChange={(v) => set('IsAnonymous', v)} />
                  <Checkbox label="Global" checked={!!form.IsGlobal} onChange={(v) => set('IsGlobal', v)} />
                  <Checkbox label="Enabled" checked={!!form.Enabled} onChange={(v) => set('Enabled', v)} />
                  <Checkbox label="Not Disabled" checked={!form.Disabled} onChange={(v) => set('Disabled', !v)} />
                  <Checkbox label="Installed" checked={!!form.Installed} onChange={(v) => set('Installed', v)} />
                </div>
              </fieldset>
            </div>
          )}

          {!loading && activeTab === 'instructions' && agentId && <InstructionsPanel agentId={agentId} />}
          {!loading && activeTab === 'templates' && agentId && <TemplatesPanel agentId={agentId} />}
          {!loading && activeTab === 'rules' && agentId && <RulesPanel agentId={agentId} />}
          {!loading && activeTab === 'hooks' && agentId && <HooksPanel agentId={agentId} />}
          {!loading && activeTab === 'capabilities' && agentId && <CapabilitiesPanel agentId={agentId} />}
          {!loading && activeTab === 'quotas' && agentId && <QuotasPanel agentId={agentId} />}
          {!loading && activeTab === 'policies' && agentId && <PoliciesPanel agentId={agentId} />}
          {!loading && activeTab === 'paymentRules' && agentId && <PaymentRulesPanel agentId={agentId} />}
          {!loading && activeTab === 'dataSamples' && agentId && <DataSamplesPanel agentId={agentId} />}
          {!loading && activeTab === 'scopes' && agentId && <ScopesPanel agentId={agentId} />}
          {!loading && activeTab === 'versions' && agentId && <VersionsPanel agentId={agentId} />}
        </div>

        <div className="aaa-editor-footer">
          <div>
            {agentId && (
              <button type="button" className="aaa-btn aaa-btn-danger" onClick={() => setConfirmDelete(true)} disabled={saving}>
                <TrashIcon size={13} /> Delete Agent
              </button>
            )}
          </div>
          <div className="aaa-editor-footer-actions">
            <button type="button" className="aaa-btn aaa-btn-default" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving…' : isEditing0 ? 'Create Agent' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete Agent"
        message={`Delete "${form.Name}"? This soft-deletes the agent — it will no longer appear in search or active lists.`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setConfirmDelete(false)}
      />
    </Modal>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number | null | undefined; onChange: (v: number | null) => void }) {
  return (
    <div className="aaa-form-group">
      <label>{label}</label>
      <input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value ? parseInt(e.target.value, 10) : null)} />
    </div>
  );
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="aaa-checkbox">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
